import { IMonitorable } from '../monitoring/IMonitorable';
import { Tag, ITag } from '../tagging/Tag';
import { Job } from '../job/Job';
import { Endorsement } from './Endorsement';

export enum ExportStatus {
  Unknown,
  exporting,
  done
}

export interface IExport extends IMonitorable {
  id: string;
  status: ExportStatus;
  projectId: string;
  name: string;
}

export enum OutputType {
  Pdf = "PDF",
  Tiff = "TIFF",
  Text = "Text",
  Native = "Native",
  NativeWithPlaceholder = "Native with Placeholder",
  NativePdf = "Native PDF"
}

export enum ColorSetting {
  BlackAndWhite = "Black & White",
  Color = "Color",
  ColorTaggedOnly = "Color Tagged Only"
}

export enum TiffExportColorFileFormat {
  TIF = "TIF",
  JPG = "JPG"
}

export class Export implements IExport {
  public id: string;
  public projectId: string;
  public status: ExportStatus;
  public job: Job;
  public name: string;
  public numQueued: number;
  public numProcessed: number;
  public startValue: number;
  public prefix: string;
  public delimiter: string;
  public zeroPaddingLength: number;
  public filesPerDirectory: number;
  public collectionIds: string[];
  public collectionNames: string[];
  public tags: { and: ITag[], or: ITag[], not: ITag[] };
  public loadFileFormatIds: string[];
  public relativitySettingsId: string;
  public loadFileFormatNames: string[];
  public colorSetting: ColorSetting;
  public tiffExportColorFileFormat: TiffExportColorFileFormat;
  public colorTagId: string;
  public outputTypes: OutputType[];
  public outputCustomNativeFilename: boolean;
  public outputOriginalNativePaths: boolean;
  public spreadsheetPlaceholder: boolean;
  public presentationPlaceholder: boolean;
  public tagPlaceholderEnabled: boolean;
  public tagPlaceholder: Tag | null;
  public virusPlaceholderText: string;
  public nativeFormatPlaceholderText: string;
  public failedToImagePlaceholderText: string;
  public containerExportTag: ITag | null;
  public includeContainerExportTag: boolean;

  public southWestEndorsement: Array<{ id: string, name: string } | Endorsement>;
  public southEndorsement: Array<{ id: string, name: string } | Endorsement>;
  public southEastEndorsement: Array<{ id: string, name: string } | Endorsement>;

  public includeRedactedResources: boolean;
  public includeResourcesWithProcessingExceptions: boolean;
  public includeResourcesWithZeroPages: boolean;
  public includeDuplicateResources: boolean;
  public keepPageOrientation: boolean;
  public directToRelativity: boolean;

  public constructor(projectId: string, {
      id = "",
      name = "",
      prefix = "iio",
      delimiter = "-",
      startValue = 1,
      zeroPaddingLength = 6,
      filesPerDirectory = 1000,
      collectionIds = [],
      tags = {and: [], or: [], not: []},
      loadFileFormatIds = [],
      relativitySettingsId = "",
      colorSetting = ColorSetting.BlackAndWhite,
      tiffExportColorFileFormat = TiffExportColorFileFormat.TIF,
      colorTagId = "",
      outputCustomNativeFilename = false,
      outputOriginalNativePaths = false,
      spreadsheetPlaceholder = false,
      presentationPlaceholder = false,
      southWestEndorsement = undefined,
      southEndorsement = undefined,
      southEastEndorsement = undefined,
      tagPlaceholderEnabled = false,
      tagPlaceholder = new Tag(projectId),
      containerExportTag = null,
      includeContainerExportTag = false,
      outputTypes = new Array<OutputType>(),
      virusPlaceholderText = "Not produced due to virus",
      nativeFormatPlaceholderText = "Produced in native format",
      failedToImagePlaceholderText = "Produced in native format",
      includeRedactedResources = true,
      includeResourcesWithProcessingExceptions = true,
      includeResourcesWithZeroPages = true,
      includeDuplicateResources = true,
      keepPageOrientation = false,
      collectionNames = [],
      loadFileFormatNames = [],
      directToRelativity = false
    }) {
      this.id = id;
      this.projectId = projectId;
      this.name = name;
      this.startValue = startValue;
      this.prefix = prefix;
      this.delimiter = delimiter;
      this.filesPerDirectory = filesPerDirectory;
      this.collectionIds = collectionIds;
      this.tags = tags;
      this.loadFileFormatIds = loadFileFormatIds;
      this.relativitySettingsId = relativitySettingsId;
      this.zeroPaddingLength = zeroPaddingLength;
      this.colorSetting = colorSetting;
      this.tiffExportColorFileFormat = tiffExportColorFileFormat;
      this.colorTagId = colorTagId;
      this.outputCustomNativeFilename = outputCustomNativeFilename;
      this.outputOriginalNativePaths = outputOriginalNativePaths;
      this.spreadsheetPlaceholder = spreadsheetPlaceholder;
      this.presentationPlaceholder = presentationPlaceholder;
      this.southWestEndorsement = southWestEndorsement;
      this.southEndorsement = southEndorsement;
      this.southEastEndorsement = southEastEndorsement;
      this.outputTypes = outputTypes;
      this.tagPlaceholderEnabled = tagPlaceholderEnabled;
      this.tagPlaceholder = tagPlaceholder;
      this.containerExportTag = containerExportTag;
      this.includeContainerExportTag = includeContainerExportTag;
      this.virusPlaceholderText = virusPlaceholderText;
      this.failedToImagePlaceholderText = failedToImagePlaceholderText;
      this.nativeFormatPlaceholderText = nativeFormatPlaceholderText;
      this.includeDuplicateResources = includeDuplicateResources;
      this.includeRedactedResources = includeRedactedResources;
      this.includeResourcesWithProcessingExceptions = includeResourcesWithProcessingExceptions;
      this.includeResourcesWithZeroPages = includeResourcesWithZeroPages;
      this.keepPageOrientation = keepPageOrientation;
      this.collectionNames = collectionNames;
      this.loadFileFormatNames = loadFileFormatNames;
      this.directToRelativity = directToRelativity;
    }
  }
